import { ICategory } from '@Hotels-at-Home/hah-fe-core/dist/types/category';
import { find, get } from 'lodash';
import { IBaseProduct } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { INTL_SITES } from '~/constants/siteConfig';

export const homepageHrefLangTags = [
  {
    hrefLang: 'de-de',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/de`,
  },
  {
    hrefLang: 'fr-fr',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/fr`,
  },
  {
    hrefLang: 'en-us',
    href: 'https://www.collectrenaissance.com',
  },
  {
    hrefLang: 'en-gb',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'en-ie',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'es-es',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/es`,
  },
  {
    hrefLang: 'en',
    href: 'https://www.europe.collectrenaissance.com',
  },
  {
    hrefLang: 'es',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/es`,
  },
  {
    hrefLang: 'fr',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/fr`,
  },
  {
    hrefLang: 'de',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/de`,
  },
];

export const hrefOrder = [
  'en-us',
  'en-ie',
  'en-gb',
  'en',
  'fr',
  'fr-fr',
  'de',
  'de-de',
  'es',
  'es-es',
  'zh-cn',
  'en-cn',
  'x-default',
];

interface Map {
  [key: string]: string | undefined;
}

export interface HrefLink {
  region: string;
  links: LinkItem[];
}

export interface LinkItem {
  value: string;
  url: string;
}

export interface HrefMap {
  href: string;
  hreflang?: string;
  rel: string;
}

export const filterExternalHrefData = (hrefData: HrefMap[]): HrefMap[] =>
  hrefData.filter(
    (item: HrefMap) =>
      item.hreflang !== 'en-ie' &&
      item.hreflang !== 'en-gb' &&
      item.hreflang !== 'fr-fr' &&
      item.hreflang !== 'de-de' &&
      item.hreflang !== 'es-es' &&
      item.hreflang !== 'x-default',
  );

export const generateEuHrefData = (
  languageUrls: { href: string; value: string }[],
  siteUrl: string,
  pathPrefix: string,
): { href: string; url: string; rel: string }[] => {
  if (!languageUrls) {
    return [];
  }

  const euHrefData: { href: string; url: string; rel: string }[] = [
    ...languageUrls.map(({ href, value }) => ({
      href,
      url: `${siteUrl}/${href}${pathPrefix}${value}`,
      rel: 'alternate',
    })),
  ];

  const enEuHref = euHrefData?.find(item => item.href === 'en')?.url;
  const frEuHref = euHrefData?.find(item => item.href === 'fr')?.url;
  const deEuHref = euHrefData?.find(item => item.href === 'de')?.url;
  const esEuHref = euHrefData?.find(item => item.href === 'es')?.url;

  euHrefData.push(
    {
      href: 'en-ie',
      url: enEuHref,
      rel: 'alternate',
    },
    {
      href: 'en-gb',
      url: enEuHref,
      rel: 'alternate',
    },
    {
      href: 'fr-fr',
      url: frEuHref,
      rel: 'alternate',
    },
    {
      href: 'de-de',
      url: deEuHref,
      rel: 'alternate',
    },
    {
      href: 'es-es',
      url: esEuHref,
      rel: 'alternate',
    },
    {
      href: 'x-default',
      url: enEuHref,
      rel: 'alternate',
    },
  );

  // remove hreflang en from euHrefData
  const enHrefIndex = euHrefData.findIndex((item: HrefMap) => item.href === 'en');
  if (enHrefIndex !== -1) {
    euHrefData.splice(enHrefIndex, 1);
  }

  return euHrefData;
};

export function removeHrefFromData(
  hrefData: HrefMap[],
  euHrefData: HrefMap[],
  enHrefIndex: number,
): void {
  if (enHrefIndex !== -1) {
    const enHref = hrefData[enHrefIndex];

    const euEnHrefIndex = euHrefData.findIndex((item: HrefMap) => item.href === enHref.hreflang);

    if (euEnHrefIndex !== -1) {
      euHrefData.splice(euEnHrefIndex, 1);
    }
  }
}

export const getHrefData = (
  item: IBaseProduct | ICategory,
  locale: string,
  useFallbacks = false,
) => {
  const hrefLangs: Map = {
    en:
      get(item, 'hreflang_en-us', null) ??
      get(item, 'hreflang_en_us', null) ??
      get(item, 'hreflang_en_US', null),
    'de-de': item?.hreflang_de,
    'en-gb': get(item, 'hreflang_en_ie', null),
    'en-ie': useFallbacks
      ? get(item, 'hreflang_en_ie', null) ??
        get(item, 'hreflang_en', find(homepageHrefLangTags, { hrefLang: 'en' })?.href)
      : get(item, 'hreflang_en_ie', null) ?? get(item, 'hreflang_en', null),
    'en-us': useFallbacks
      ? get(item, 'hreflang_en_US', null) ??
        get(item, 'hreflang_en_us', null) ??
        find(homepageHrefLangTags, { hrefLang: 'en-us' })?.href
      : get(item, 'hreflang_en_US', null) ?? get(item, 'hreflang_en_us', null),
    'en-ca': useFallbacks
      ? get(item, 'hreflang_en_CA', null) ??
        get(item, 'hreflang_en_ca', null) ??
        find(homepageHrefLangTags, { hrefLang: 'en-ca' })?.href
      : get(item, 'hreflang_en_CA', null) ?? get(item, 'hreflang_en_ca', null),
    'zh-cn': useFallbacks
      ? get(item, 'hreflang_zh_cn', null) ?? find(homepageHrefLangTags, { hrefLang: 'zh-cn' })?.href
      : get(item, 'hreflang_zh_cn', null),
    'en-cn': useFallbacks
      ? get(item, 'hreflang_en_CN', null) ??
        get(item, 'hreflang_en_cn', null) ??
        find(homepageHrefLangTags, { hrefLang: 'en-cn' })?.href
      : get(item, 'hreflang_en_CN', null) ?? get(item, 'hreflang_en_cn', null),
    'fr-fr': item?.hreflang_fr,
    'es-es': item?.hreflang_es,
    'x-default': useFallbacks
      ? get(item, 'hreflang_en_US', null) ??
        get(item, 'hreflang_en_us', null) ??
        find(homepageHrefLangTags, { hrefLang: 'en-us' })?.href
      : get(item, 'hreflang_en_US', null) ??
        get(item, 'hreflang_en_us', null) ??
        get(item, 'hreflang_x_default', null),
  };

  const hrefData: HrefMap[] = [];

  hrefOrder.forEach(key => {
    if (hrefLangs[key] !== null) {
      hrefData.push({
        href: hrefLangs[key],
        hreflang: key,
        rel: 'alternate',
      });
    }
  });

  return hrefData;
};

export function extractHrefLinks(item: IBaseProduct | ICategory): LinkItem[] {
  const createLink = (link: string | null) => {
    if (!link) return '';
    return link.includes('?') ? `${link}&referrer=eu` : `${link}?referrer=eu`;
  };

  const usLink = createLink(
    get(item, 'hreflang_en_US', null) ??
      get(item, 'hreflang_en_us', null) ??
      find(homepageHrefLangTags, { hrefLang: 'en-us' }).href,
  );

  return [
    {
      value: 'U.S.A.',
      url: usLink,
    },
  ];
}

export function getHrefLinks(
  isHomepage: boolean,
  product: IBaseProduct,
  category: ICategory,
): HrefLink[] {
  if (isHomepage) {
    return INTL_SITES;
  }

  const item = product || category;
  const result = item ? extractHrefLinks(item) : [];

  const [naLinkObj, apacLinkObj = { links: [] as LinkItem[] }] = INTL_SITES ?? [];

  return [
    {
      region: 'NA',
      links: result.length ? result : naLinkObj.links,
    },
    {
      region: 'APAC',
      links: apacLinkObj.links,
    },
  ];
}
